import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "../../../contexts/AuthContext";
import { toast } from "sonner";
import { RefreshCw, ClipboardList, AlertCircle } from "lucide-react";
import { ProgressHeader } from "./ProgressHeader";
import { TaskGroup, TaskCard, CATEGORY_CONFIG } from "./TaskComponents";

const LORA  = { fontFamily: "'Lora', Georgia, serif" };
const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const CATEGORY_ORDER = ["documents", "sante", "finances", "logistique", "bagages", "numerique", "securite"];

// ── Skeleton loader ───────────────────────────────────────────────────────────
function SkeletonLoader() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="h-4 bg-[#DCD4C6] rounded w-48 mb-6" />
      <div className="w-full bg-[#DCD4C6] rounded-full h-2 mb-6" />
      {[1, 2, 3].map((g) => (
        <div key={g} className="space-y-2">
          <div className="h-3 bg-[#DCD4C6] rounded w-24 mb-3" />
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3 p-3 border border-[#DCD4C6] rounded-sm">
              <div className="w-4 h-4 bg-[#DCD4C6] rounded" />
              <div className="flex-1 h-4 bg-[#DCD4C6] rounded" />
              <div className="w-16 h-4 bg-[#DCD4C6] rounded" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Generating state ──────────────────────────────────────────────────────────
function GeneratingState() {
  return (
    <div className="py-12 text-center">
      <div className="w-10 h-10 border-2 border-[#C17F59] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <p className="text-[#5C554E] text-sm" style={LORA}>
        Votre conseiller prépare votre checklist personnalisée...
      </p>
      <p className="text-[#9A8F86] text-xs mt-2 italic" style={LORA}>
        Cela prend environ 10 secondes
      </p>
    </div>
  );
}

// ── Failed state ──────────────────────────────────────────────────────────────
function FailedState({ onRetry, retrying }) {
  return (
    <div className="py-12 text-center">
      <AlertCircle size={32} strokeWidth={1.5} className="text-red-400 mx-auto mb-4" />
      <p className="text-[#5C554E] text-sm mb-4" style={LORA}>
        La génération a échoué. Voulez-vous réessayer ?
      </p>
      <button
        onClick={onRetry}
        disabled={retrying}
        className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#C17F59] text-[#F9F6F0] text-sm hover:bg-[#A86A46] disabled:opacity-50 transition-colors"
        style={{ ...LORA, borderRadius: "2px" }}
        data-testid="checklist-retry-btn"
      >
        <RefreshCw size={14} strokeWidth={1.5} className={retrying ? "animate-spin" : ""} />
        {retrying ? "Génération..." : "Réessayer"}
      </button>
    </div>
  );
}

// ── All urgent rows (imminent departure) ──────────────────────────────────────
function ImminentView({ tasks, onToggle, loadingIds }) {
  const sorted = [...tasks].sort((a, b) => {
    const prio = { en_retard: 0, urgent: 1, a_venir: 2, completee: 3 };
    return (prio[a.urgence] ?? 4) - (prio[b.urgence] ?? 4);
  });
  return (
    <div>
      {sorted.map((task) => (
        <div key={task.id} className="mb-2">
          <TaskCard task={task} onToggle={onToggle} isLoading={loadingIds.has(task.id)} />
        </div>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function PreDepartureTab({ tripId, dateDepart }) {
  const [data, setData] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingIds, setLoadingIds] = useState(new Set());
  const [retrying, setRetrying] = useState(false);
  const pollRef = React.useRef(null);

  const fetchChecklist = useCallback(async (silent = false) => {
    try {
      const { data: resp } = await axios.get(`${API}/trips/${tripId}/checklist`);
      setData(resp);
      if (!silent) setInitialLoading(false);

      // Poll while generating
      if (resp.status === "generating") {
        pollRef.current = setTimeout(() => fetchChecklist(true), 3000);
      }
    } catch {
      if (!silent) setInitialLoading(false);
    }
  }, [tripId]);

  useEffect(() => {
    fetchChecklist();
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [fetchChecklist]);

  const handleToggle = useCallback(async (taskId, completed) => {
    setLoadingIds((prev) => new Set([...prev, taskId]));
    try {
      await axios.patch(`${API}/trips/${tripId}/checklist/${taskId}`, { completed });
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          tasks: prev.tasks.map((t) =>
            t.id === taskId
              ? { ...t, completed, completed_at: completed ? new Date().toISOString() : null,
                  urgence: completed ? "completee" : t.urgence }
              : t
          ),
        };
      });
    } catch {
      toast.error("Impossible de mettre à jour la tâche");
    } finally {
      setLoadingIds((prev) => { const n = new Set(prev); n.delete(taskId); return n; });
    }
  }, [tripId]);

  const handleRetry = useCallback(async () => {
    setRetrying(true);
    try {
      await axios.post(`${API}/trips/${tripId}/checklist/regenerate`);
      setData((prev) => ({ ...prev, status: "generating" }));
      setTimeout(() => fetchChecklist(true), 3000);
    } catch {
      toast.error("Impossible de relancer la génération");
    } finally {
      setRetrying(false);
    }
  }, [tripId, fetchChecklist]);

  // Group tasks by category (preserving category order)
  const tasksByCategory = useMemo(() => {
    const groups = {};
    for (const cat of CATEGORY_ORDER) { groups[cat] = []; }
    for (const task of data?.tasks ?? []) {
      const cat = task.categorie in groups ? task.categorie : "logistique";
      groups[cat].push(task);
    }
    return groups;
  }, [data?.tasks]);

  const allTasks = data?.tasks ?? [];
  const isAllUrgent =
    allTasks.length > 0 &&
    allTasks.filter((t) => !t.completed).every((t) => t.urgence === "en_retard");

  if (initialLoading) return <SkeletonLoader />;

  if (!data || data.status === "not_available") {
    return (
      <div className="py-12 text-center">
        <ClipboardList size={32} strokeWidth={1.5} className="text-[#DCD4C6] mx-auto mb-4" />
        <p className="text-[#9A8F86] text-sm italic" style={LORA}>
          La checklist n'est pas encore disponible pour ce voyage.
        </p>
      </div>
    );
  }

  if (data.status === "generating") return <GeneratingState />;

  if (data.status === "generation_failed") {
    return <FailedState onRetry={handleRetry} retrying={retrying} />;
  }

  return (
    <div data-testid="predeparture-tab">
      {/* Header with progress */}
      <ProgressHeader
        tasks={allTasks}
        dateDepart={dateDepart}
        isImminent={data.is_imminent}
        imminentMessage={data.imminent_message}
      />

      {/* Tasks by category */}
      {isAllUrgent ? (
        // All en_retard — flat list without grouping
        <ImminentView tasks={allTasks} onToggle={handleToggle} loadingIds={loadingIds} />
      ) : (
        CATEGORY_ORDER.map((cat) =>
          tasksByCategory[cat]?.length > 0 ? (
            <TaskGroup
              key={cat}
              category={cat}
              tasks={tasksByCategory[cat]}
              onToggle={handleToggle}
              loadingIds={loadingIds}
            />
          ) : null
        )
      )}

      {/* Generated at notice */}
      {data.generated_at && (
        <p className="text-xs text-[#9A8F86] italic text-right mt-4" style={LORA}>
          Checklist générée le {new Date(data.generated_at).toLocaleDateString("fr-FR")}
        </p>
      )}
    </div>
  );
}
